import { useCallback } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import { createGuid } from 'utils';
import {
  addCertification,
  getCertificationDetails,
  updateCertification,
  setOnboarding,
} from 'modules/state-mgmt/onboarding';
import { transformCertificationForEdit } from '../utils/transformations';
import { CertificationFormData } from '../types';
import { getImageFormat } from 'utils/files';

export const useCertificationOperations = (
  typeOptions: any[],
  modalState: any,
  setEditData: (data: any) => void,
  setLoading: (loading: boolean) => void,
  closeModal: () => void,
) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { workerId, projectId, languageCode, isOffline, isWorker, onboarding, isAppOffline } = useAppSelector(
    state => ({
      workerId: state.onboarding.id,
      projectId: state.onboarding?.temp?.project?.value,
      languageCode: state.app?.languageCode,
      isOffline: !state.app.isOnline || state.onboarding?.uploadingDetails?.isOffline,
      isAppOffline: !state.app.isOnline,
      isWorker: !state.user?.userId || state.user?.isWorker,
      onboarding: state.onboarding,
    }),
  );

  const prepareCertificationPayload = useCallback(
    (certificationData: CertificationFormData) => {
      const selectedCertType =
        certificationData.type && typeof certificationData.type === 'object' && 'value' in certificationData.type
          ? certificationData.type.value
          : null;

      return {
        certificationId: selectedCertType?.id || '',
        idNumber: certificationData.certificationId || '',
        description: certificationData.description || '',
        completionDate: certificationData.issueDate ? moment(certificationData.issueDate).format('YYYY-MM-DD') : '',
        expirationDate: certificationData.expirationDate
          ? moment(certificationData.expirationDate).format('YYYY-MM-DD')
          : null,
        projectId: projectId || null,
        workerId: workerId || '',
      };
    },
    [projectId, workerId],
  );

  const prepareImagesForUpload = useCallback((images: any[], isEdit = false) => {
    return (
      images
        ?.filter(image => {
          if (isEdit) {
            return (image.image || image.url) && !image.isExisting;
          }
          return image.image || image.url;
        })
        .map(image => {
          const imageData = image.image || image.url;
          const imageFormat = getImageFormat(imageData);
          return {
            tempId: image.tempId,
            image: imageData,
            fileName: image.fileName || `${createGuid()}.${imageFormat}`,
          };
        }) || []
    );
  }, []);

  const addNewCertification = useCallback(
    async (payload: any, images: any[]) => {
      if (isOffline && !isWorker) {
        // Offline: Add to onboarding state
        const selectedCertType = typeOptions.find(t => t.value.id === payload.certificationId)?.value;
        const newImages = images.map(img => ({
          id: createGuid(),
          url: img.image || img.url,
          fileName: img.fileName || `${createGuid()}.png`,
        }));

        const newCert = {
          id: createGuid(),
          idNumber: payload.idNumber,
          description: payload.description,
          completionDate: payload.completionDate,
          expirationDate: payload.expirationDate,
          createdAt: moment().toISOString(),
          lastUpdatedAt: moment().toISOString(),
          certification: {
            id: payload.certificationId,
            name: selectedCertType?.name || '',
            isExpirationDateMandatory: selectedCertType?.isExpirationDateMandatory || false,
          },
          files: newImages,
          project: { id: projectId || '' },
          alias: null,
          aliasesInProjects: [],
          projectVisibility: 0,
          isOffline: true,
          tempId: createGuid(),
        };

        const currentItems = (onboarding.allCertifications as any)?.items || [];
        const updatedItems = [...currentItems, newCert];
        const updatedAllCertifications = { ...(onboarding.allCertifications || {}), items: updatedItems };

        dispatch(
          setOnboarding({
            allCertifications: updatedAllCertifications,
            uploadingDetails: {
              ...onboarding.uploadingDetails,
              isOffline: true,
              pendingUploads: { ...onboarding.uploadingDetails?.pendingUploads, certifications: true },
            },
          }),
        );
        return;
      }

      try {
        const imagesToUpload = prepareImagesForUpload(images, false);

        await dispatch(
          addCertification({
            workerId,
            certificationData: payload,
            images: imagesToUpload,
            languageCode,
          }),
        ).unwrap();
      } catch (error) {
        const errorMessage = t('certifications.errors.failedToAdd', 'Failed to add certification');
        enqueueSnackbar(errorMessage, { variant: 'error' });
        throw error;
      }
    },
    [
      dispatch,
      workerId,
      languageCode,
      prepareImagesForUpload,
      t,
      isOffline,
      isWorker,
      typeOptions,
      onboarding,
      projectId,
    ],
  );

  const updateExistingCertification = useCallback(
    async (payload: any, images: any[], deletedFileIds: string[] = []) => {
      if (isOffline && !isWorker) {
        const currentItems = (onboarding.allCertifications as any)?.items || [];
        const certToUpdateIndex = currentItems.findIndex((c: any) => c.id === modalState.editCertificationId);

        if (certToUpdateIndex > -1) {
          const certToUpdate = { ...currentItems[certToUpdateIndex] };
          const selectedCertType = typeOptions.find(t => t.value.id === payload.certificationId)?.value;

          // Handle images
          let certImages = images || [];
          // Remove deleted (soft delete if offline and has id)
          certImages = certImages.map((f: any) => (deletedFileIds.includes(f.id) ? { ...f, isDeleted: true } : f));

          certToUpdate.idNumber = payload.idNumber;
          certToUpdate.description = payload.description;
          certToUpdate.completionDate = payload.completionDate;
          certToUpdate.expirationDate = payload.expirationDate;
          certToUpdate.lastUpdatedAt = moment().toISOString();
          certToUpdate.isOffline = true;
          certToUpdate.certification = {
            id: payload.certificationId,
            name: selectedCertType?.name || certToUpdate.certification.name,
            isExpirationDateMandatory: selectedCertType?.isExpirationDateMandatory || false,
          };
          certToUpdate.files = certImages;

          const updatedItems = [...currentItems];
          updatedItems[certToUpdateIndex] = certToUpdate;
          const updatedAllCertifications = { ...(onboarding.allCertifications || {}), items: updatedItems };

          dispatch(
            setOnboarding({
              allCertifications: updatedAllCertifications,
              uploadingDetails: {
                ...onboarding.uploadingDetails,
                isOffline: true,
                pendingUploads: { ...onboarding.uploadingDetails?.pendingUploads, certifications: true },
              },
            }),
          );
        }
        return;
      }

      try {
        const imagesToUpload = prepareImagesForUpload(images, true);

        await dispatch(
          updateCertification({
            workerId,
            certificationId: modalState.editCertificationId,
            certificationData: payload,
            images: imagesToUpload,
            deletedFileIds,
            languageCode,
          }),
        ).unwrap();
      } catch (error) {
        const errorMessage = t('certifications.errors.failedToUpdate', 'Failed to update certification');
        enqueueSnackbar(errorMessage, { variant: 'error' });
        throw error;
      }
    },
    [
      dispatch,
      workerId,
      languageCode,
      modalState.editCertificationId,
      prepareImagesForUpload,
      t,
      isOffline,
      isWorker,
      onboarding,
      typeOptions,
    ],
  );

  const saveCertification = useCallback(
    async (certificationData: CertificationFormData) => {
      try {
        if (!workerId && !onboarding.uploadingDetails?.isOffline && !isWorker) {
          throw new Error('Worker ID not available');
        }

        const payload = prepareCertificationPayload(certificationData);

        if (modalState.mode === 'edit') {
          await updateExistingCertification(payload, certificationData.images, certificationData.deletedFileIds || []);
        } else {
          await addNewCertification(payload, certificationData.images);
        }

        closeModal();
      } catch (error) {
        const errorMessage = t('certifications.errors.failedToSave', 'Failed to save certification');
        enqueueSnackbar(errorMessage, { variant: 'error' });
        throw error;
      }
    },
    [
      workerId,
      modalState.mode,
      prepareCertificationPayload,
      updateExistingCertification,
      addNewCertification,
      closeModal,
      t,
    ],
  );

  const loadCertificationDetails = useCallback(
    async (certificateId: string) => {
      const currentItems = (onboarding.allCertifications as any)?.items || [];
      const cert = currentItems.find((c: any) => c.id === certificateId);

      if ((isAppOffline || cert?.tempId) && !isWorker) {
        if (cert) {
          const editFormData = transformCertificationForEdit(cert, projectId, typeOptions);
          setEditData(editFormData);
        }
        return;
      }

      try {
        if (!workerId) {
          throw new Error('Worker ID not available');
        }

        const certificationDetails = await dispatch(
          getCertificationDetails({
            workerId,
            certificationId: certificateId,
            languageCode,
          }),
        ).unwrap();

        const editFormData = transformCertificationForEdit(certificationDetails, projectId, typeOptions);
        setEditData(editFormData);
      } catch (error) {
        const errorMessage = t('certifications.errors.failedToLoadDetails', 'Failed to load certification details');
        enqueueSnackbar(errorMessage, { variant: 'error' });
        throw error;
      }
    },
    [dispatch, workerId, languageCode, projectId, typeOptions, setEditData, t, isOffline, isWorker, onboarding],
  );

  return {
    saveCertification,
    loadCertificationDetails,
  };
};
